import {
  Suspense,
  lazy,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getBlockedEmojis } from "./useAllowedEmojis";
import { getHiddenEmojiUnifieds } from "./chatReactionUtils";
import "./chatReactions.scss";

const EmojiPicker = lazy(() => import("emoji-picker-react"));

const GAP = 6;
const EDGE = 8;

const HIDDEN_STYLE = { visibility: "hidden" };

const getScrollParent = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY)) return node;
    node = node.parentElement;
  }
  return null;
};

// Union of the avatar box and everything drawn around it (frame, badges),
// since decorative frames overflow the avatar element itself.
const getAvatarRect = (anchor) => {
  const avatar = anchor
    .closest(".chat-message-row")
    ?.querySelector(".chat-framed-avatar");
  if (!avatar) return null;

  const rects = [avatar, ...avatar.querySelectorAll("*")]
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width && r.height);
  if (!rects.length) return null;

  return {
    left: Math.min(...rects.map((r) => r.left)),
    right: Math.max(...rects.map((r) => r.right)),
    top: Math.min(...rects.map((r) => r.top)),
    bottom: Math.max(...rects.map((r) => r.bottom)),
  };
};

const computePosition = (anchor, picker) => {
  const anchorRect = anchor.getBoundingClientRect();
  const width = picker.offsetWidth;
  const height = picker.offsetHeight;

  const viewport = {
    left: EDGE,
    right: window.innerWidth - EDGE,
    top: EDGE,
    bottom: window.innerHeight - EDGE,
  };
  const scrollRect = getScrollParent(anchor)?.getBoundingClientRect();
  const bounds = scrollRect
    ? {
        left: Math.max(viewport.left, scrollRect.left + EDGE),
        right: Math.min(viewport.right, scrollRect.right - EDGE),
        top: Math.max(viewport.top, scrollRect.top),
        bottom: Math.min(viewport.bottom, scrollRect.bottom),
      }
    : viewport;

  // Message scrolled out of the chat container: keep the picker out of sight.
  if (anchorRect.bottom < bounds.top || anchorRect.top > bounds.bottom) {
    return null;
  }

  const avatar = getAvatarRect(anchor);
  const anchorCenter = (anchorRect.left + anchorRect.right) / 2;
  const avatarOnLeft = avatar
    ? (avatar.left + avatar.right) / 2 < anchorCenter
    : anchorCenter < (bounds.left + bounds.right) / 2;

  // Horizontal: start at the bubble edge next to the avatar and grow away
  // from it, staying clear of the avatar column when there is room.
  let minLeft = bounds.left;
  let maxRight = bounds.right;
  let clearsAvatarSideways = !avatar;
  if (avatar) {
    const avatarEdgeMin = avatarOnLeft ? avatar.right + GAP : minLeft;
    const avatarEdgeMax = avatarOnLeft ? maxRight : avatar.left - GAP;
    if (avatarEdgeMax - avatarEdgeMin >= width) {
      minLeft = avatarEdgeMin;
      maxRight = avatarEdgeMax;
      clearsAvatarSideways = true;
    }
  }

  let left = avatarOnLeft ? anchorRect.left : anchorRect.right - width;
  left = Math.max(minLeft, Math.min(left, maxRight - width));
  left = Math.max(viewport.left, Math.min(left, viewport.right - width));

  // Vertical: above the bubble, flipped below when there is no room.
  // If the picker could not clear the avatar sideways, clear it vertically.
  let refTop = anchorRect.top;
  let refBottom = anchorRect.bottom;
  if (!clearsAvatarSideways && avatar) {
    refTop = Math.min(refTop, avatar.top);
    refBottom = Math.max(refBottom, avatar.bottom);
  }

  const above = refTop - GAP - height;
  const below = refBottom + GAP;
  let top;
  if (above >= bounds.top) top = above;
  else if (below + height <= bounds.bottom) top = below;
  else if (above >= viewport.top) top = above;
  else if (below + height <= viewport.bottom) top = below;
  else top = viewport.top;

  return { top, left };
};

const FULL_PICKER_WIDTH = 340;
const FULL_PICKER_HEIGHT = 400;

// Touch/long-press on the picker must not reach the bubble's swipe-to-reply
// handlers (React events bubble through the portal to the message).
const stopPropagation = (e) => e.stopPropagation();
const ISOLATE_EVENTS = {
  onTouchStart: stopPropagation,
  onTouchMove: stopPropagation,
  onTouchEnd: stopPropagation,
  onTouchCancel: stopPropagation,
  onContextMenu: stopPropagation,
};

const getFullPickerSize = () => ({
  width: Math.min(FULL_PICKER_WIDTH, window.innerWidth - EDGE * 2),
  height: Math.min(FULL_PICKER_HEIGHT, window.innerHeight - EDGE * 2),
});

const ChatReactionPicker = ({
  emojis = [],
  selected = null,
  onPick,
  anchorRef,
}) => {
  const { t } = useTranslation("common");
  const pickerRef = useRef(null);
  const [style, setStyle] = useState(HIDDEN_STYLE);
  const [fullSize, setFullSize] = useState(null);
  const expanded = Boolean(fullSize);

  // WhatsApp-style: a current reaction outside the quick bar is shown after it.
  const quickEmojis = useMemo(
    () =>
      selected && !emojis.includes(selected) ? [...emojis, selected] : emojis,
    [emojis, selected]
  );
  const hiddenEmojis = useMemo(
    () => (expanded ? getHiddenEmojiUnifieds(getBlockedEmojis()) : []),
    [expanded]
  );

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    const picker = pickerRef.current;
    if (!anchor || !picker) return;
    const pos = computePosition(anchor, picker);
    setStyle(pos ? { top: pos.top, left: pos.left } : HIDDEN_STYLE);
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!emojis.length) return undefined;
    updatePosition();

    let frame = null;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        updatePosition();
      });
    };
    const handleResize = () => {
      if (expanded) setFullSize(getFullPickerSize());
      schedule();
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("scroll", schedule, true);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("scroll", schedule, true);
    };
  }, [emojis.length, expanded, fullSize, updatePosition]);

  if (!emojis.length) return null;

  const pick = (emoji) => {
    if (emoji) onPick?.(emoji);
  };

  return createPortal(
    <div
      key={expanded ? "full" : "quick"}
      ref={pickerRef}
      className={`chat-reaction-picker${
        expanded ? " chat-reaction-picker--full" : ""
      }`}
      role={expanded ? "dialog" : "listbox"}
      style={expanded ? { ...style, ...fullSize } : style}
      {...ISOLATE_EVENTS}
    >
      {expanded ? (
        <Suspense fallback={<div className="chat-reaction-picker__loading" />}>
          <EmojiPicker
            width="100%"
            height="100%"
            theme="dark"
            emojiStyle="native"
            lazyLoadEmojis
            autoFocusSearch={false}
            hiddenEmojis={hiddenEmojis}
            previewConfig={{ showPreview: false }}
            searchPlaceholder={t("chatMessages.searchEmoji", "Search emoji")}
            onEmojiClick={(emojiData) => pick(emojiData?.emoji)}
          />
        </Suspense>
      ) : (
        <>
          {quickEmojis.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={`chat-reaction-picker__emoji${
                selected === emoji ? " is-selected" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                pick(emoji);
              }}
            >
              {emoji}
            </button>
          ))}
          <button
            type="button"
            className="chat-reaction-picker__more"
            aria-label={t("chatMessages.moreReactions", "More reactions")}
            title={t("chatMessages.moreReactions", "More reactions")}
            onClick={(e) => {
              e.stopPropagation();
              setFullSize(getFullPickerSize());
            }}
          >
            <PlusOutlined />
          </button>
        </>
      )}
    </div>,
    document.body
  );
};

ChatReactionPicker.propTypes = {
  emojis: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.string,
  onPick: PropTypes.func,
  anchorRef: PropTypes.shape({ current: PropTypes.any }),
};

export default ChatReactionPicker;
